import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Client-side TTL cache for the AI Templates gallery, persisted to localStorage.
 *
 * The gallery used to hit the database on every visit (and every search
 * keystroke). This store keeps the last-fetched templates (keyed by the search
 * term) and categories in localStorage with a 12-hour TTL, so after a single
 * visit the page serves straight from cache with zero network calls until the
 * entry expires. A full reload or a new browser reuses the same cache because
 * it lives in localStorage.
 *
 * To keep localStorage lean we only ever store what the gallery needs and let
 * entries expire naturally; clearing the site data (or waiting out the TTL)
 * forces a fresh fetch.
 */
const TTL = 12 * 60 * 60 * 1000; // 12 hours

const isFresh = (entry) => !!entry && (Date.now() - entry.at) < TTL;

export const useAiTemplateCacheStore = create(
    persist(
        (set, get) => ({
            // { [searchKey]: { data: Template[], at: number } }
            templatesByKey: {},
            // { data: Category[], at: number } | null
            categories: null,

            /** Returns cached templates for `key` only if still within the TTL. */
            getFreshTemplates: (key) => {
                const entry = get().templatesByKey[key];
                return isFresh(entry) ? entry.data : undefined;
            },
            /** Returns cached categories only if still within the TTL. */
            getFreshCategories: () => {
                const entry = get().categories;
                return isFresh(entry) ? entry.data : undefined;
            },

            setTemplates: (key, data) =>
                set((s) => ({
                    templatesByKey: { ...s.templatesByKey, [key]: { data, at: Date.now() } },
                })),
            setCategories: (data) => set({ categories: { data, at: Date.now() } }),

            /** Wipe the cache (e.g. after the user edits/creates a template). */
            clear: () => set({ templatesByKey: {}, categories: null }),
        }),
        {
            name: 'ai-template-cache',
            version: 2, // v2: list items gained trendingCount (7-day usage) — drop older caches that lack it
            partialize: (s) => ({ templatesByKey: s.templatesByKey, categories: s.categories }),
        }
    )
);
